import { useEffect, useRef, useState } from 'react';
import { baixarBlob } from '../../api';
import { numero, telefoneDigitadoLegivel } from '../../formato';
import { FotoProtegida, LinkProtegido } from '../../ui';
import { FotoDoContato } from './FotoDoContato';

/**
 * As mídias novas da mensagem — onda 555 (WhatsApp completo, parte A-1,
 * Tarefa 4; spec §3.1, quadro A1): o vídeo, a figurinha, a localização e o
 * cartão de contato. A API os entrega na `MensagemDaLinha` (Tarefa 3); a
 * `Mensagem` da conversa despacha pelo tipo.
 *
 *  · **Vídeo** — o player da casa (`<video controls preload="metadata">`)
 *    pelos bytes da rota de arquivo de hoje (`baixarBlob`, que deixa o
 *    `video/mp4`), com a duração no canto («0:48») e «toque para assistir
 *    aqui mesmo». Só baixa quando o balão chega perto da tela.
 *  · **Figurinha** — a imagem, sem balão.
 *  · **Localização** — o mapa pequeno e estático: as telhas do satélite da
 *    Esri, as mesmas do fundo «Satélite» do `MapaDaCampanha`
 *    (`TELHAS_DA_ESRI`, em `mapaGeotecnico/estilo.ts`), com o alfinete no
 *    centro; sem telha, o quadro neutro com o alfinete. «Abrir no mapa» abre
 *    o Google Maps em outra aba; «Usar como local da obra» (A13, §3.1.1) só
 *    vem de quem a pode oferecer.
 *  · **Cartão de contato** — o nome, o telefone legível e a legenda, com
 *    «Gravar contato» e «Nova conversa»; o número que já é de um contato
 *    troca «Gravar contato» por «Abrir o contato».
 */

export interface ArquivoDaMensagem { id: string; nome: string; mime: string; tamanho: number }
export interface LocalizacaoDaLinha { latitude: number; longitude: number; nome: string | null; endereco: string | null }
export interface CartaoDaLinha { nome: string | null; telefones: string[]; contatoId: string | null }

/** «0:48», «12:05», «1:02:03». */
export function minutosESegundos(s: number): string {
  const t = Math.max(0, Math.round(s));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const r = String(t % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`;
}

/** A coordenada como se lê, quando a localização não trouxe endereço: «-26,91940 · -49,06610». */
export const coordenada = (latitude: number, longitude: number) => `${numero(latitude, 5)} · ${numero(longitude, 5)}`;

// ── O vídeo ──────────────────────────────────────────────────────────

/** Quanto antes da borda o vídeo começa a baixar: a pessoa rola e ele já está lá. */
const ANTES_DE_APARECER = '200px';

/** O que rola em volta do elemento (a linha do tempo, no PC); no celular, a página (`null`). */
function rolagemDe(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const o = getComputedStyle(p).overflowY;
    if ((o === 'auto' || o === 'scroll') && p.scrollHeight > p.clientHeight) return p;
  }
  return null;
}

/**
 * O vídeo só baixa quando o balão chega perto da tela (até 25 MB cada: a conversa com vários vídeos, ou o
 * histórico, puxaria centenas de MB, dados do celular). Até lá, e enquanto baixa, fica o quadro com o
 * play; a duração aparece quando o navegador lê o começo do arquivo.
 */
export function VideoDaMensagem({ arquivo }: { arquivo: ArquivoDaMensagem }) {
  const quadro = useRef<HTMLDivElement>(null);
  const [perto, setPerto] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [falhou, setFalhou] = useState<'baixar' | 'tocar' | null>(null);
  const [duracao, setDuracao] = useState<number | null>(null);

  useEffect(() => {
    setPerto(false);
    const el = quadro.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setPerto(true); return; }
    const olho = new IntersectionObserver((l) => {
      if (l.some((x) => x.isIntersecting)) { setPerto(true); olho.disconnect(); }
    }, { root: rolagemDe(el), rootMargin: `${ANTES_DE_APARECER} 0px` });
    olho.observe(el);
    return () => olho.disconnect();
  }, [arquivo.id]);

  useEffect(() => {
    setSrc(null); setFalhou(null); setDuracao(null);
    if (!perto) return;
    const abortar = new AbortController();
    let endereco: string | null = null;
    baixarBlob(`/arquivos/${arquivo.id}/conteudo`, abortar.signal)
      .then((b) => { endereco = URL.createObjectURL(b); setSrc(endereco); })
      .catch((e) => { if ((e as Error).name !== 'AbortError') setFalhou('baixar'); });
    return () => { abortar.abort(); if (endereco) URL.revokeObjectURL(endereco); };
  }, [arquivo.id, perto]);

  if (falhou) {
    const baixe = (
      <LinkProtegido caminho={`/arquivos/${arquivo.id}/conteudo`} nomeQueda={arquivo.nome}>Baixe o arquivo</LinkProtegido>
    );
    return (
      <p className="msg-aux msg-video-falhou">
        {falhou === 'tocar'
          ? <>O vídeo não abriu neste navegador (formato que ele não toca). {baixe} para ver.</>
          : <>O vídeo não carregou (a conexão falhou). {baixe} para tentar de novo.</>}
      </p>
    );
  }
  return (
    <div className="msg-video">
      <div className="msg-video-quadro" ref={quadro}>
        {src ? (
          <video controls preload="metadata" src={src} aria-label="Vídeo"
            onLoadedMetadata={(e) => { const d = e.currentTarget.duration; if (Number.isFinite(d)) setDuracao(d); }}
            onError={() => setFalhou('tocar')} />
        ) : (
          <div className="msg-video-capa" role="img" aria-label="Vídeo" aria-busy={perto}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
          </div>
        )}
        {duracao !== null && <span className="msg-video-duracao">{minutosESegundos(duracao)}</span>}
      </div>
      <span className="msg-aux">Vídeo · toque para assistir aqui mesmo</span>
    </div>
  );
}

// ── A figurinha ──────────────────────────────────────────────────────

export function FigurinhaDaMensagem({ arquivo }: { arquivo: ArquivoDaMensagem }) {
  return (
    <FotoProtegida arquivoId={arquivo.id} alt="Figurinha" className="msg-figurinha-imagem"
      carregando={<span className="msg-aux">Carregando a figurinha</span>}
      semImagem={<span className="msg-aux">Figurinha</span>} />
  );
}

// ── A localização ────────────────────────────────────────────────────

/** As telhas do satélite da Esri, as do fundo «Satélite» do mapa (`TELHAS_DA_ESRI`, `mapaGeotecnico/estilo.ts`). */
const TELHA = (z: number, x: number, y: number) =>
  `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;
const ZOOM = 16;
const LADO = 256;
/** O quadro tem até 240 × 120 px: as telhas que o cobrem, com o ponto no centro. */
const LARGURA = 240;
const ALTURA = 120;

/** As telhas em volta do ponto, com o deslocamento de cada uma a partir do centro do quadro. */
export function telhasEmVolta(latitude: number, longitude: number) {
  const n = 2 ** ZOOM;
  const px = ((longitude + 180) / 360) * n * LADO;
  const lat = (Math.max(-85, Math.min(85, latitude)) * Math.PI) / 180;
  const py = ((1 - Math.log(Math.tan(lat) + 1 / Math.cos(lat)) / Math.PI) / 2) * n * LADO;
  const lista: { url: string; dx: number; dy: number }[] = [];
  for (let x = Math.floor((px - LARGURA / 2) / LADO); x <= Math.floor((px + LARGURA / 2) / LADO); x++) {
    for (let y = Math.floor((py - ALTURA / 2) / LADO); y <= Math.floor((py + ALTURA / 2) / LADO); y++) {
      lista.push({ url: TELHA(ZOOM, ((x % n) + n) % n, y), dx: Math.round(x * LADO - px), dy: Math.round(y * LADO - py) });
    }
  }
  return lista;
}

/** A coordenada que dá para pôr no mapa: os dois números finitos, dentro da Terra. */
export const coordenadaValida = (latitude: unknown, longitude: unknown): boolean =>
  typeof latitude === 'number' && typeof longitude === 'number' && Number.isFinite(latitude) && Number.isFinite(longitude)
  && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180;

/** O Google Maps no ponto; sem coordenada válida, nada (a localização mostra só o endereço). */
export const linkDoMapa = (latitude: number, longitude: number): string | null =>
  coordenadaValida(latitude, longitude) ? `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}` : null;

function MapaPequeno({ latitude, longitude }: { latitude: number; longitude: number }) {
  const [semTelha, setSemTelha] = useState(false);
  useEffect(() => { setSemTelha(false); }, [latitude, longitude]);
  if (!coordenadaValida(latitude, longitude)) return null;
  return (
    <div className={`msg-mapa${semTelha ? ' sem-telha' : ''}`} aria-hidden="true">
      {!semTelha && telhasEmVolta(latitude, longitude).map((t) => (
        <img key={t.url} src={t.url} alt="" className="msg-mapa-telha" loading="lazy" decoding="async"
          referrerPolicy="no-referrer" style={{ left: `calc(50% + ${t.dx}px)`, top: `calc(50% + ${t.dy}px)` }}
          onError={() => setSemTelha(true)} />
      ))}
      <svg className="msg-mapa-alfinete" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="3.5" className="miolo" />
      </svg>
      {!semTelha && <span className="msg-mapa-credito">Imagens © Esri</span>}
    </div>
  );
}

export function LocalizacaoDaMensagem({ local, aoUsar, usando = false }: {
  local: LocalizacaoDaLinha;
  /** «Usar como local da obra»: só na recebida desta conversa, com oportunidade ligada, para quem a edita. */
  aoUsar?(): void;
  usando?: boolean;
}) {
  /* A coordenada estragada (NaN, fora da Terra): só o endereço, sem mapa e sem link. */
  const valida = coordenadaValida(local.latitude, local.longitude);
  const mapa = linkDoMapa(local.latitude, local.longitude);
  const endereco = local.endereco?.trim()
    || (valida ? coordenada(local.latitude, local.longitude) : 'A localização veio sem endereço e sem coordenada.');
  /* Sem coordenada válida, nada a gravar como local da obra. */
  const usar = valida ? aoUsar : undefined;
  return (
    <div className="msg-local">
      {valida && <MapaPequeno latitude={local.latitude} longitude={local.longitude} />}
      <strong className="msg-local-nome">{local.nome?.trim() || 'Localização'}</strong>
      <span className="msg-aux msg-local-endereco">{endereco}</span>
      {(mapa || usar) && (
        <div className="msg-acoes">
          {mapa && (
            <a className="botao secundario" href={mapa} target="_blank" rel="noopener noreferrer">Abrir no mapa</a>
          )}
          {usar && (
            <button type="button" className="botao discreto" aria-disabled={usando}
              onClick={() => { if (!usando) usar(); }}>
              {usando ? 'Gravando o local…' : 'Usar como local da obra'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── O cartão de contato ──────────────────────────────────────────────

export function CartaoDeContato({ cartao, legenda, aoGravar, aoNovaConversa, aoAbrir }: {
  cartao: CartaoDaLinha;
  /** O texto que veio com o cartão. */
  legenda: string | null;
  /** «Gravar contato» (a janela da parte B, `JanelaDoContato modo="novo"`). */
  aoGravar?(): void;
  /** «Nova conversa» com o número do cartão (`JanelaNovaConversa`). */
  aoNovaConversa?(): void;
  /** O número já é de um contato que a pessoa vê: «Abrir o contato». */
  aoAbrir?(): void;
}) {
  const telefones = cartao.telefones.map((t) => telefoneDigitadoLegivel(t)).filter(Boolean);
  const nome = cartao.nome?.trim() || telefones[0] || 'Contato sem nome';
  return (
    <div className="msg-cartao">
      <div className="msg-cartao-pessoa">
        <FotoDoContato contatoId={cartao.contatoId ?? ''} nome={cartao.nome ?? ''} temFoto={false} tamanho="p" />
        <span className="msg-cartao-texto">
          <strong>{nome}</strong>
          {telefones.map((t) => <span key={t} className="msg-aux msg-cartao-telefone">{t}</span>)}
        </span>
      </div>
      {(aoAbrir || aoGravar || aoNovaConversa) && (
        <div className="msg-acoes">
          {cartao.contatoId && aoAbrir ? (
            <button type="button" className="botao secundario" onClick={aoAbrir}>Abrir o contato</button>
          ) : aoGravar && !cartao.contatoId ? (
            <button type="button" className="botao secundario" onClick={aoGravar}>Gravar contato</button>
          ) : null}
          {aoNovaConversa && telefones.length > 0 && (
            <button type="button" className="botao discreto" onClick={aoNovaConversa}>Nova conversa</button>
          )}
        </div>
      )}
      <span className="msg-aux msg-cartao-legenda">
        Cartão de contato{legenda?.trim() ? `: «${legenda.trim()}»` : ''}
      </span>
    </div>
  );
}
